import React, { useState } from 'react';
import {
  Info, BookOpen, Settings, Users, Eye, Globe, Lock,
  Plus, X, Clock, User, Layers, BookMarked, CheckCircle,
  UsersRound, Zap, Library
} from 'lucide-react';
import './CurriculumBuilderTab.css';

const CurriculumBuilderTab: React.FC = () => {
  const [curriculumTitle, setCurriculumTitle] = useState('');
  const [curriculumDescription, setCurriculumDescription] = useState('');
  const [selectedCourseCategory, setSelectedCourseCategory] = useState('');
  const [estimatedDuration, setEstimatedDuration] = useState('0');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [enrollmentType, setEnrollmentType] = useState<'open' | 'restricted' | 'auto-enroll'>('open');
  const [targetAudience, setTargetAudience] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [sendReminders, setSendReminders] = useState(false);
  const [visibilityType, setVisibilityType] = useState<'public' | 'restricted'>('public');
  const [showAddCoursesModal, setShowAddCoursesModal] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState<number[]>([]);

  // Sample course data
  const availableCourses = [
    { id: 1, title: 'Advanced Leadership Development', duration: '8 weeks', enrolled: 456, type: 'Web-Based', color: '#4285F4' },
    { id: 2, title: 'Project Management Certification Workshop', duration: '3 days', enrolled: 87, type: 'ILT', color: '#34A853' },
    { id: 3, title: 'Data Analytics Professional Program', duration: '6 weeks', enrolled: 234, type: 'Blended', color: '#9334E9' },
  ];

  const curriculumStats = [
    { label: 'Total Curricula', value: '0', icon: Library, color: '#8200DB' },
    { label: 'Active Curricula', value: '0', icon: CheckCircle, color: '#00A045' },
    { label: 'Total Enrollments', value: '0', icon: UsersRound, color: '#0065CC' }
  ];

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleTargetAudienceChange = (audience: string) => {
    if (targetAudience.includes(audience)) {
      setTargetAudience(targetAudience.filter(a => a !== audience));
    } else {
      setTargetAudience([...targetAudience, audience]);
    }
  };

  const handleToggleCourseSelection = (courseId: number) => {
    if (selectedCourses.includes(courseId)) {
      setSelectedCourses(selectedCourses.filter(id => id !== courseId));
    } else {
      setSelectedCourses([...selectedCourses, courseId]);
    }
  };

  const handleDoneSelectingCourses = () => {
    setShowAddCoursesModal(false);
  };

  const handleResetForm = () => {
    setCurriculumTitle('');
    setCurriculumDescription('');
    setSelectedCourseCategory('');
    setEstimatedDuration('0');
    setTags([]);
    setEnrollmentType('open');
    setTargetAudience([]);
    setDueDate('');
    setSendReminders(false);
    setVisibilityType('public');
  };

  return (
    <>
      <div className="trainer-coursemanagement-curriculum-stats-grid">
        {curriculumStats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <div key={index} className="trainer-coursemanagement-curriculum-stat-card">
              <div className="trainer-coursemanagement-curriculum-stat-content">
                <div className="trainer-coursemanagement-curriculum-stat-label">{stat.label}</div>
                <div className="trainer-coursemanagement-curriculum-stat-value">{stat.value}</div>
              </div>
              <div className="trainer-coursemanagement-curriculum-stat-icon" style={{ backgroundColor: stat.color }}>
                <IconComponent size={24} color="white" />
              </div>
            </div>
          );
        })}
      </div>

      <div className="trainer-coursemanagement-curriculum-builder-layout">
        <div className="trainer-coursemanagement-curriculum-form">
          <div className="trainer-coursemanagement-curriculum-form-section">
            <div className="trainer-coursemanagement-curriculum-section-header">
              <div className="trainer-coursemanagement-curriculum-section-icon">
                <Info size={24} color="white" />
              </div>
              <div>
                <h3>Basic Information</h3>
                <p className="trainer-coursemanagement-curriculum-section-subtitle">Curriculum details and description</p>
              </div>
            </div>

            <div className="trainer-coursemanagement-curriculum-form-group">
              <label>Curriculum Title *</label>
              <input
                type="text"
                placeholder="e.g., Complete Leadership Development Path"
                value={curriculumTitle}
                onChange={(e) => setCurriculumTitle(e.target.value)}
                className="trainer-coursemanagement-curriculum-form-input"
              />
            </div>

            <div className="trainer-coursemanagement-curriculum-form-group">
              <label>Description *</label>
              <textarea
                placeholder="Describe the learning path and objectives..."
                value={curriculumDescription}
                onChange={(e) => setCurriculumDescription(e.target.value)}
                className="trainer-coursemanagement-curriculum-form-textarea"
                rows={4}
              />
            </div>

            <div className="trainer-coursemanagement-curriculum-form-row">
              <div className="trainer-coursemanagement-curriculum-form-group">
                <label>Category *</label>
                <select
                  value={selectedCourseCategory}
                  onChange={(e) => setSelectedCourseCategory(e.target.value)}
                  className="trainer-coursemanagement-curriculum-form-select"
                >
                  <option value="">Select Category</option>
                  <option value="Leadership">Leadership</option>
                  <option value="Technical">Technical</option>
                  <option value="Management">Management</option>
                </select>
              </div>

              <div className="trainer-coursemanagement-curriculum-form-group">
                <label>Estimated Duration</label>
                <div className="trainer-coursemanagement-curriculum-duration-input">
                  <input
                    type="number"
                    value={estimatedDuration}
                    onChange={(e) => setEstimatedDuration(e.target.value)}
                    className="trainer-coursemanagement-curriculum-form-input"
                    min="0"
                  />
                  <span className="trainer-coursemanagement-curriculum-duration-unit">weeks</span>
                </div>
              </div>
            </div>

            <div className="trainer-coursemanagement-curriculum-form-group">
              <label>Tags</label>
              <div className="trainer-coursemanagement-curriculum-tags-input-container">
                <div className="trainer-coursemanagement-curriculum-tags-list">
                  {tags.map((tag, index) => (
                    <span key={index} className="trainer-coursemanagement-curriculum-tag">
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)} className="trainer-coursemanagement-curriculum-tag-remove">×</button>
                    </span>
                  ))}
                </div>
                <div className="trainer-coursemanagement-curriculum-tag-input-wrapper">
                  <input
                    type="text"
                    placeholder="Add tags..."
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    className="trainer-coursemanagement-curriculum-tag-input"
                  />
                  <button onClick={handleAddTag} className="trainer-coursemanagement-curriculum-tag-add-btn">+</button>
                </div>
              </div>
            </div>
          </div>

          <div className="trainer-coursemanagement-curriculum-form-section">
            <div className="trainer-coursemanagement-curriculum-section-header">
              <div className="trainer-coursemanagement-curriculum-section-icon trainer-coursemanagement-curriculum-section-icon-blue">
                <BookOpen size={24} color="white" />
              </div>
              <div>
                <h3>Select Learning Item</h3>
                <p className="trainer-coursemanagement-curriculum-section-subtitle">Add courses to this curriculum</p>
              </div>
              <button
                className="trainer-coursemanagement-curriculum-add-courses-btn"
                onClick={() => setShowAddCoursesModal(true)}
              >
                <Plus size={16} /> Add Courses
              </button>
            </div>

            <div className="trainer-coursemanagement-curriculum-empty-state">
              <div className="trainer-coursemanagement-curriculum-empty-icon">
                <BookOpen size={48} />
              </div>
              <p className="trainer-coursemanagement-curriculum-empty-title">No courses added yet</p>
              <p className="trainer-coursemanagement-curriculum-empty-subtitle">Click "Add Courses" to build your curriculum</p>
            </div>
          </div>

          <div className="trainer-coursemanagement-curriculum-form-section">
            <div className="trainer-coursemanagement-curriculum-section-header">
              <div className="trainer-coursemanagement-curriculum-section-icon trainer-coursemanagement-curriculum-section-icon-orange">
                <Settings size={24} color="white" />
              </div>
              <div>
                <h3>Learning Path Settings</h3>
                <p className="trainer-coursemanagement-curriculum-section-subtitle">Configure completion rules</p>
              </div>
            </div>

            <div className="trainer-coursemanagement-curriculum-learning-type">
              <div className="trainer-coursemanagement-curriculum-learning-type-header">
                <strong>Sequential Learning</strong>
                <p>Learners must complete courses in order</p>
              </div>
              <div className="trainer-coursemanagement-curriculum-info-box">
                <span className="trainer-coursemanagement-curriculum-info-icon">
                  <Info size={18} />
                </span>
                <span>Courses will be unlocked one at a time as learners progress</span>
              </div>
            </div>
          </div>

          <div className="trainer-coursemanagement-curriculum-form-section">
            <div className="trainer-coursemanagement-curriculum-section-header">
              <div className="trainer-coursemanagement-curriculum-section-icon trainer-coursemanagement-curriculum-section-icon-green">
                <Users size={24} color="white" />
              </div>
              <div>
                <h3>Enrollment Settings</h3>
                <p className="trainer-coursemanagement-curriculum-section-subtitle">Control who can access this curriculum</p>
              </div>
            </div>

            <div className="trainer-coursemanagement-curriculum-form-group">
              <label>Enrollment Type</label>
              <div className="trainer-coursemanagement-curriculum-enrollment-options">
                <button
                  className={`trainer-coursemanagement-curriculum-enrollment-option ${enrollmentType === 'open' ? 'active' : ''}`}
                  onClick={() => setEnrollmentType('open')}
                >
                  <span className="trainer-coursemanagement-curriculum-enrollment-icon">
                    <Globe size={18} />
                  </span>
                  <span>Open</span>
                </button>
                <button
                  className={`trainer-coursemanagement-curriculum-enrollment-option ${enrollmentType === 'restricted' ? 'active' : ''}`}
                  onClick={() => setEnrollmentType('restricted')}
                >
                  <span className="trainer-coursemanagement-curriculum-enrollment-icon">
                    <Lock size={18} />
                  </span>
                  <span>Restricted</span>
                </button>
                <button
                  className={`trainer-coursemanagement-curriculum-enrollment-option ${enrollmentType === 'auto-enroll' ? 'active' : ''}`}
                  onClick={() => setEnrollmentType('auto-enroll')}
                >
                  <span className="trainer-coursemanagement-curriculum-enrollment-icon">
                    <Zap size={18} />
                  </span>
                  <span>Auto-Enroll</span>
                </button>
              </div>
            </div>

            <div className="trainer-coursemanagement-curriculum-form-group">
              <label>Target Audience</label>
              <div className="trainer-coursemanagement-curriculum-checkbox-group">
                {['All Users', 'New Hires', 'Managers', 'Sales Team', 'Engineering'].map((audience) => (
                  <label key={audience} className="trainer-coursemanagement-curriculum-checkbox-label">
                    <input
                      type="checkbox"
                      checked={targetAudience.includes(audience)}
                      onChange={() => handleTargetAudienceChange(audience)}
                    />
                    <span>{audience}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="trainer-coursemanagement-curriculum-form-row">
              <div className="trainer-coursemanagement-curriculum-form-group">
                <label>Due Date (Optional)</label>
                <input
                  type="text"
                  placeholder="dd-mm-yyyy"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="trainer-coursemanagement-curriculum-form-input"
                />
              </div>
              <div className="trainer-coursemanagement-curriculum-form-group">
                <label>Send Reminders</label>
                <label className="trainer-coursemanagement-curriculum-toggle-switch">
                  <input
                    type="checkbox"
                    checked={sendReminders}
                    onChange={(e) => setSendReminders(e.target.checked)}
                  />
                  <span className="trainer-coursemanagement-curriculum-toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="trainer-coursemanagement-curriculum-form-section">
            <div className="trainer-coursemanagement-curriculum-section-header">
              <div className="trainer-coursemanagement-curriculum-section-icon trainer-coursemanagement-curriculum-section-icon-gray">
                <Eye size={24} color="white" />
              </div>
              <div>
                <h3>Visibility & Access Control</h3>
                <p className="trainer-coursemanagement-curriculum-section-subtitle">Control who can see and access this course</p>
              </div>
            </div>

            <div className="trainer-coursemanagement-curriculum-visibility-options">
              <button
                className={`trainer-coursemanagement-curriculum-visibility-option ${visibilityType === 'public' ? 'active' : ''}`}
                onClick={() => setVisibilityType('public')}
              >
                <div className="trainer-coursemanagement-curriculum-visibility-icon">
                  <Globe size={24} />
                </div>
                <div className="trainer-coursemanagement-curriculum-visibility-content">
                  <strong>Public / Organization-Wide</strong>
                  <p>Anyone in your organization can see and access this course</p>
                </div>
              </button>

              <button
                className={`trainer-coursemanagement-curriculum-visibility-option ${visibilityType === 'restricted' ? 'active' : ''}`}
                onClick={() => setVisibilityType('restricted')}
              >
                <div className="trainer-coursemanagement-curriculum-visibility-icon">
                  <Lock size={24} />
                </div>
                <div className="trainer-coursemanagement-curriculum-visibility-content">
                  <strong>Restricted Access</strong>
                  <p>Only specific groups, departments, or roles can access this course</p>
                </div>
              </button>
            </div>

            {visibilityType === 'public' && (
              <div className="trainer-coursemanagement-curriculum-info-box trainer-coursemanagement-curriculum-info-box-green">
                <span className="trainer-coursemanagement-curriculum-info-icon">
                  <Globe size={18} />
                </span>
                <div>
                  <strong>Organization-Wide Access</strong>
                  <p>This course will be visible and accessible to all users in your organization. They can enroll based on enrollment settings.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="trainer-coursemanagement-curriculum-preview">
          <h3>Curriculum Preview</h3>

          <div className="trainer-coursemanagement-curriculum-preview-card">
            <div className="trainer-coursemanagement-curriculum-preview-icon">
              <Library size={32} />
            </div>
            <h4>Curriculum Title</h4>
            <p className="trainer-coursemanagement-curriculum-preview-description">Curriculum description will appear here...</p>

            <div className="trainer-coursemanagement-curriculum-preview-details">
              <div className="trainer-coursemanagement-curriculum-preview-detail">
                <span className="trainer-coursemanagement-curriculum-preview-label">Category:</span>
                <span className="trainer-coursemanagement-curriculum-preview-value">{selectedCourseCategory || 'Not selected'}</span>
              </div>
              <div className="trainer-coursemanagement-curriculum-preview-detail">
                <span className="trainer-coursemanagement-curriculum-preview-label">Courses:</span>
                <span className="trainer-coursemanagement-curriculum-preview-value">0</span>
              </div>
              <div className="trainer-coursemanagement-curriculum-preview-detail">
                <span className="trainer-coursemanagement-curriculum-preview-label">Duration:</span>
                <span className="trainer-coursemanagement-curriculum-preview-value">{estimatedDuration} weeks</span>
              </div>
              <div className="trainer-coursemanagement-curriculum-preview-detail">
                <span className="trainer-coursemanagement-curriculum-preview-label">Learning Path:</span>
                <span className="trainer-coursemanagement-curriculum-preview-value">Sequential</span>
              </div>
              <div className="trainer-coursemanagement-curriculum-preview-detail">
                <span className="trainer-coursemanagement-curriculum-preview-label">Enrollment:</span>
                <span className="trainer-coursemanagement-curriculum-preview-value">{enrollmentType === 'open' ? 'Open' : enrollmentType === 'restricted' ? 'Restricted' : 'Auto-Enroll'}</span>
              </div>
            </div>

            <button className="trainer-coursemanagement-curriculum-create-curriculum-btn">
              <Library size={18} /> Create Curriculum
            </button>
            <button className="trainer-coursemanagement-curriculum-reset-btn" onClick={handleResetForm}>Reset Form</button>
          </div>
        </div>
      </div>

      {/* Add Courses Modal */}
      {showAddCoursesModal && (
        <div className="trainer-curriculum-modal-overlay" onClick={() => setShowAddCoursesModal(false)}>
          <div className="trainer-curriculum-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="trainer-curriculum-modal-header">
              <div>
                <h2>Add Courses to Curriculum</h2>
                <p>Select courses to include in your curriculum</p>
              </div>
              <button
                className="trainer-curriculum-modal-close-btn"
                onClick={() => setShowAddCoursesModal(false)}
              >
                <X size={24} />
              </button>
            </div>

            <div className="trainer-curriculum-modal-body">
              {availableCourses.map((course) => (
                <div
                  key={course.id}
                  className={`trainer-curriculum-course-item ${selectedCourses.includes(course.id) ? 'selected' : ''}`}
                  onClick={() => handleToggleCourseSelection(course.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedCourses.includes(course.id)}
                    onChange={() => {}}
                    className="trainer-curriculum-course-checkbox"
                  />
                  <div
                    className="trainer-curriculum-course-icon"
                    style={{ backgroundColor: course.color }}
                  >
                    <Globe size={24} color="white" />
                  </div>
                  <div className="trainer-curriculum-course-info">
                    <h4>{course.title}</h4>
                    <div className="trainer-curriculum-course-meta">
                      <span className="trainer-curriculum-course-duration">
                        <Clock size={14} /> {course.duration}
                      </span>
                      <span className="trainer-curriculum-course-enrolled">
                        <User size={14} /> {course.enrolled} enrolled
                      </span>
                      <span
                        className="trainer-curriculum-course-type"
                        style={{
                          backgroundColor: course.type === 'Web-Based' ? '#E3F2FD' : course.type === 'ILT' ? '#E8F5E9' : '#F3E5F5',
                          color: course.type === 'Web-Based' ? '#1976D2' : course.type === 'ILT' ? '#388E3C' : '#7B1FA2'
                        }}
                      >
                        {course.type}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="trainer-curriculum-modal-footer">
              <div className="trainer-curriculum-modal-footer-info">
                {selectedCourses.length} course(s) selected
              </div>
              <button
                className="trainer-curriculum-modal-done-btn"
                onClick={handleDoneSelectingCourses}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CurriculumBuilderTab;
